import { categorizeServiceActivity, type ServiceActivityCategory } from "@shared/serviceActivityCategory";
import { formatElapsedSeconds, stopReasonLabel } from "@shared/roWorkTimer";

type TimerAuditValue = {
  startedAt?: string | null;
  stoppedAt?: string | null;
  continuationConfirmedAt?: string | null;
  durationSeconds?: number | null;
  reason?: string | null;
  notes?: string | null;
  source?: string | null;
  technicianName?: string | null;
  to?: string | null;
};

export type RoAuditPresentation = {
  action: string;
  detail: string | null;
  category: ServiceActivityCategory;
};

function parseValue(value: unknown): TimerAuditValue | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as TimerAuditValue) : null;
  } catch {
    return null;
  }
}

export function presentRoAuditEntry(
  entry: { action: string; newValue?: unknown },
  formatDate: (value: string) => string,
): RoAuditPresentation {
  const category = categorizeServiceActivity(entry.action);
  const value = parseValue(entry.newValue);
  if (!value) return { action: entry.action.replace(/_/g, " "), detail: null, category };

  if (entry.action === "technician_assigned") {
    const name = typeof value.technicianName === "string" ? value.technicianName : null;
    return { action: "Assigned technician", detail: name, category };
  }

  if (entry.action === "status_changed") {
    const to = typeof value.to === "string" ? value.to.replace(/_/g, " ") : null;
    return { action: to ? `Status changed to ${to}` : "Status changed", detail: null, category };
  }

  if (entry.action === "repair_started_without_approval") {
    return { action: "Started repair without customer approval", detail: value.reason ? `Reason: ${value.reason}. Customer decision remains outstanding.` : "Customer decision remains outstanding.", category };
  }

  if (entry.action === "work_session_started") {
    return {
      action: "started the RO work timer",
      detail: value.startedAt ? `Started ${formatDate(value.startedAt)}` : null,
      category,
    };
  }

  if (
    entry.action === "work_session_stopped"
    || entry.action === "work_session_admin_recovery_stop"
    || entry.action === "work_session_automatic_eod_stop"
    || entry.action === "work_session_automatic_final_cutoff"
  ) {
    const parts = [
      value.startedAt ? `Started ${formatDate(value.startedAt)}` : null,
      value.stoppedAt ? `Stopped ${formatDate(value.stoppedAt)}` : null,
      typeof value.durationSeconds === "number" ? `Duration ${formatElapsedSeconds(value.durationSeconds)}` : null,
      value.reason ? stopReasonLabel(value.reason, value.source ?? null) : null,
      value.notes?.trim() || null,
    ].filter(Boolean);
    const action = entry.action === "work_session_admin_recovery_stop"
      ? "stopped the RO work timer using Admin recovery"
      : entry.action === "work_session_automatic_eod_stop" || entry.action === "work_session_automatic_final_cutoff"
        ? "automatically stopped the RO work timer"
        : "stopped the RO work timer";
    return {
      action,
      detail: parts.join(" · ") || null,
      category,
    };
  }

  if (entry.action === "work_session_continuation_confirmed") {
    return {
      action: "confirmed continued work after 6:00 PM",
      detail: value.continuationConfirmedAt ? `Confirmed ${formatDate(value.continuationConfirmedAt)}` : null,
      category,
    };
  }

  return { action: entry.action.replace(/_/g, " "), detail: null, category };
}
